import React, { useState, useRef, useCallback } from 'react'
import { Badge, Box, Button, Popover, Tooltip, styled } from '@mui/material'
import { Icon as Iconify } from '@iconify/react'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { appColors } from 'utils'
import PinButtonDropdown from './PinButtonDropdown'

const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    top: 0,
    right: 0,
    backgroundColor: appColors.dirNptColor,
    fontSize: '.7rem',
    height: '14px',
    minWidth: '14px',
    padding: 0,
  },
}))

const PinButton = ({
  name = 'Filters',
  icon,
  pinnedItemOptions,
  pinnedGridFilters,
  setPinnedGridFilters,
  pinnedFiltersActive,
  onClear,
}) => {
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [anchorPos, setAnchorPos] = useState(null)
  const [hoveringBadge, setHoveringBadge] = useState(false)
  const { getRibbonBackgroundColor, getTextColor, getWindowBarColor } = useInnovaTheme()

  const handleOpen = () => {
    if (!anchorRef.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    setAnchorPos({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    })
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  const onPinnedChange = useCallback(
    (newPinned) => {
      setPinnedGridFilters(newPinned)
    },
    [setPinnedGridFilters],
  )

  const handleClearClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (typeof onClear === 'function') onClear()
    setHoveringBadge(false)
  }

  const showClearX = Boolean(pinnedFiltersActive) && hoveringBadge

  const pinButton = (
    <Button
      ref={anchorRef}
      variant='outlined'
      size='small'
      onClick={handleOpen}
      sx={{
        minWidth: 0,
        width: 28,
        height: 28,
        padding: 0,
        borderRadius: 1.5,
        border: '2px solid',
        color: getTextColor(),
        borderColor: appColors.borderColorDark,
        lineHeight: 1.2,
      }}>
      {icon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>}
    </Button>
  )

  const clearButton = (
    <Tooltip title='Clear all pinned filters' arrow placement='right'>
      <Button
        size='small'
        sx={{
          minWidth: 0,
          width: '14px',
          height: '14px',
          padding: 0,
          borderRadius: 1.5,
          color: 'black',
          borderColor: appColors.borderColorDark,
          lineHeight: 1.2,
        }}
        onClick={handleClearClick}>
        <Iconify icon='mdi:close' width={14} height={14} />
      </Button>
    </Tooltip>
  )

  return (
    <>
      <Box
        component='span'
        sx={{ display: 'inline-block', position: 'relative' }}
        onMouseEnter={() => setHoveringBadge(true)}
        onMouseLeave={() => setHoveringBadge(false)}>
        {pinnedFiltersActive ? (
          <StyledBadge
            badgeContent={showClearX ? clearButton : pinnedFiltersActive}
            showZero={false}
            overlap='rectangular'
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
            {pinButton}
          </StyledBadge>
        ) : (
          pinButton
        )}
      </Box>

      <Popover
        id={`${name}-filters-popover`}
        open={open && !!anchorPos}
        anchorReference='anchorPosition'
        anchorPosition={anchorPos || { top: 0, left: 0 }}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: getRibbonBackgroundColor(),
              border: `2px solid ${getWindowBarColor()}`,
              p: 1,
              width: 'auto',
              borderRadius: 2,
              overflow: 'visible',
            },
          },
        }}>
        <PinButtonDropdown items={pinnedItemOptions} pinned={pinnedGridFilters} onChange={onPinnedChange} />
      </Popover>
    </>
  )
}

export default PinButton
